import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ManageFiltersModal } from './ManageFiltersModal';
import { SavedFilter } from '../../types';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { SaveFilterModal, FormValues } from './SaveFilterModal';
import { Settings } from '@lifeomic/chromicons';
import { useFilterProcessingState } from '../../FilterProcessingStateContext';
import { useHashParams } from '@lifeomic/phc-web-toolkit/src/hooks/useHashParams';
import { UseHydrateOmicsExplorer } from '../../useHydrateOmicsExplorer';
import { useManageSavedFilters } from './useManageSavedFilters';
import { useSelectedFilter } from '../../SelectedFilterContext';
import { useSelectedFilterOptions } from '../../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import * as React from 'react';
import clsx from 'clsx';
import useToggle from '@lifeomic/phc-web-toolkit/src/hooks/useToggle';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks';

const useStyles = makeStyles(theme => ({
  root: {},
  filterBox: {
    marginBottom: theme.spacing(1),
    position: 'relative',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  actionButton: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  containerActionButton: {
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(0.5),
  },
  lastActionButton: {
    paddingRight: 0,
  },
}));

const messages = defineMessages({
  manageFilters: {
    id: 'savedFiltersControls.manageFilters',
    defaultMessage: 'Manage filters',
  },
});
interface SavedFiltersControlsProps {
  state?: UseHydrateOmicsExplorer['state'];
}

export const SavedFiltersControls: React.FC<SavedFiltersControlsProps> = ({
  state,
}) => {
  const classes = useStyles({});

  const {
    currentFilterBasedOnSequenceAndVariantType,
    currentDefaultFilter,
    onCreateNewFilter,
    onDeleteFilter,
    onUpdateFilter,
    onUpdateDefaultFilter,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  } = useManageSavedFilters({ state });
  const [filterProcessingState] = useFilterProcessingState();
  const [selectedFilter, setSelectedFilter] = useSelectedFilter();

  const [, onHashParamPush] = useHashParams();
  const { formatMessage } = useIntl();
  const [canPerform, permissions] = useABAC();

  const hasSaveFilterPermissions =
    canPerform(permissions.readData) ||
    canPerform(permissions.readMaskedData) ||
    canPerform(permissions.projectAdmin);

  // Filter hooks
  const [selectedFilterOptions, setSelectedFilterOptions] =
    useSelectedFilterOptions();

  // Internal State
  const [isCreateOrUpdateModalOpen, setIsCreateOrUpdateModalOpen] =
    React.useState(false);

  const [isManageFiltersOpen, openManageFilters, closeManageFilters] =
    useToggle(false);

  // Handlers
  const handleCreateNewFilter = async ({
    name,
    description,
    setAsDefaultFilter,
  }: FormValues) => {
    const response = await onCreateNewFilter({
      name,
      description,
      setAsDefaultFilter,
    });
    if (response?.status === 200) {
      setIsCreateOrUpdateModalOpen(false);
    }
  };

  const handleDeleteFilter = async (filter: SavedFilter) => {
    await onDeleteFilter(filter);
  };

  const handleUpdateFilter = async (
    filter: SavedFilter,
    setAsDefaultFilter: boolean,
  ) => {
    const response = await onUpdateFilter(filter, setAsDefaultFilter);

    if (response?.status === 200) {
      setIsCreateOrUpdateModalOpen(false);
    }
  };

  const handleResetClicked = () => {
    if (!selectedFilter?.params?.filters) {
      return;
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setSelectedFilterOptions(selectedFilter.params.filters);
    onHashParamPush({
      filters: JSON.stringify(selectedFilter.params.filters),
    });
  };

  const handleClearAllClicked = () => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setSelectedFilter(null);
    setSelectedFilterOptions([]);
    onHashParamPush({ filters: undefined });
  };

  const handleSavedFilterClick = (filter: SavedFilter) => {
    setSelectedFilter(filter);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setSelectedFilterOptions(filter?.params?.filters || []);
  };

  return (
    <React.Fragment>
      <Box className={classes.filterBox} align="center" fullWidth>
        <SavedFiltersDropdown
          isLoading={
            state === 'INITIALIZING' || filterProcessingState === 'INITIALIZING'
          }
          onFilterMenuItemClick={handleSavedFilterClick}
          options={currentFilterBasedOnSequenceAndVariantType}
          currentDefaultFilterId={currentDefaultFilter?.id}
        />
        <IconButton
          aria-label={formatMessage(messages.manageFilters)}
          icon={Settings}
          size={0}
          onClick={openManageFilters}
          paddingRight={0}
          aria-haspopup="dialog"
          aria-expanded={isManageFiltersOpen}
          disabled={
            state === 'INITIALIZING' ||
            currentFilterBasedOnSequenceAndVariantType.length === 0
          }
        />
      </Box>
      <div className={classes.buttonsContainer}>
        <div>
          {hasSaveFilterPermissions && (
            <Button
              className={clsx(
                classes.actionButton,
                classes.containerActionButton,
              )}
              aria-haspopup="dialog"
              aria-expanded={isCreateOrUpdateModalOpen}
              variant="text"
              onClick={() => setIsCreateOrUpdateModalOpen(true)}
              disabled={
                state === 'INITIALIZING' || selectedFilterOptions.length === 0
              }
            >
              <FormattedMessage
                id="omicsExplorer.savedFiltersControls.saveFilters"
                defaultMessage="Save"
              />
            </Button>
          )}

          <Button
            className={clsx(
              classes.actionButton,
              classes.containerActionButton,
            )}
            variant="text"
            onClick={handleResetClicked}
            disabled={!selectedFilter}
          >
            <FormattedMessage
              id="omicsExplorer.savedFiltersControls.reset"
              defaultMessage="Reset"
            />
          </Button>
        </div>
        <Button
          className={clsx(classes.actionButton, classes.lastActionButton)}
          variant="text"
          onClick={handleClearAllClicked}
          disabled={!selectedFilter && selectedFilterOptions?.length === 0}
        >
          <FormattedMessage
            id="omicsExplorer.savedFiltersControls.clearAll"
            defaultMessage="Clear All"
          />
        </Button>
      </div>
      {isCreateOrUpdateModalOpen && (
        <SaveFilterModal
          savedFilter={selectedFilter}
          onDismiss={() => setIsCreateOrUpdateModalOpen(false)}
          onCreateNewFilter={({ name, description, setAsDefaultFilter }) =>
            handleCreateNewFilter({ name, description, setAsDefaultFilter })
          }
          onUpdateFilter={(filter, setAsDefaultFilter) =>
            handleUpdateFilter(filter, setAsDefaultFilter)
          }
          isDefaultFilterAlready={
            currentDefaultFilter?.id === selectedFilter?.id
          }
          isOpen
        />
      )}
      {isManageFiltersOpen && (
        <ManageFiltersModal
          currentDefaultFilter={currentDefaultFilter}
          isOpen={isManageFiltersOpen}
          onDismiss={closeManageFilters}
          savedFilters={currentFilterBasedOnSequenceAndVariantType}
          onDeleteFilter={handleDeleteFilter}
          onUpdateDefaultFilter={onUpdateDefaultFilter}
        />
      )}
    </React.Fragment>
  );
};
